// lib
import React, { useReducer, useState, useRef, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { isEqual } from 'lodash'

import { dataReducer } from '@aytm/app/stats/page/question-views/components/mapping/reducers'

// components
import { Dropdown } from 'primereact/dropdown'

import { default as ComponentContainer } from '@aytm/app/stats/page/question-views/components/mapping/Component'

import Entities from '@aytm/app/stats/page/question-views/components/mapping/Entities'
import Attributes from '@aytm/app/stats/page/question-views/components/mapping/Attributes'
import Variables from '@aytm/app/stats/page/question-views/components/mapping/Variables'

import Controls from '@aytm/app/stats/page/question-views/components/mapping/Controls'
import Sources from '@aytm/app/stats/page/question-views/components/mapping/Sources'
import Footer from '@aytm/app/stats/page/question-views/components/mapping/Footer'

// styles
import style from '@aytm/app/stats/page/question-views/components/styles/Component.module.scss'

const Component = ({
  model,
  data,
  states,
  disabled,
  onChange,
  onReset,
  onControlsChange,
  onModeChange,
  onUpdateData,
  onFetchApiKey,
  onPublish,
  onPublished
}) => {
  const [state, dispatch] = useReducer(dataReducer, {
    entities: JSON.parse(JSON.stringify(model.entities)),
    attributes: JSON.parse(JSON.stringify(model.attributes)),
    variables: JSON.parse(JSON.stringify(model.variables)),
    refs: {},
    errors: []
  })

  const [changed, setChanged] = useState(false)

  const timer = useRef(null)

  const stateRef = useRef()
  stateRef.current = state

  const waveRef = useRef(null)
  waveRef.current = String(model.wave)

  const showReset = useMemo(
    () => !model.original && !disabled && 'function' == typeof onReset,
    [model.original, disabled, onReset]
  )

  const attributesOptions = useMemo(
    () =>
      data.options.attributes.map(attribute => {
        const option = {
          icon: attribute.icon,
          label: attribute.label,
          search: attribute.search
        }

        const refs = state.entities.map(entity => {
          const i = data.options.entities.findIndex(v => v.pattern == entity.pattern)
          return attribute.refs[i]
        })

        option.key = refs.join(', ')
        option.text = `<b>${option.label}</b><br/>${option.key}`

        return option
      }),
    [data.options.entities, data.options.attributes, state.entities]
  )

  const itemTemplate = option => <span dangerouslySetInnerHTML={{ __html: option.text }}></span>
  const valueTemplate = (option, _props) => (
    <span
      dangerouslySetInnerHTML={{ __html: option ? option.label || option.text : _props.placeholder || 'Select...' }}
    ></span>
  )

  const checkModel = () => {
    const newData = {}
    if (!isEqual(state.entities, model.entities)) {
      newData.errors ||= JSON.parse(JSON.stringify(state.errors))
      for (const i in state.entities) {
        const index = newData.errors.indexOf(`e${Number(i) + 1}`)
        0 <= index && newData.errors.splice(index, 1)
      }
      newData.entities = JSON.parse(JSON.stringify(model.entities))
    }
    if (!isEqual(state.attributes, model.attributes)) {
      newData.errors ||= JSON.parse(JSON.stringify(state.errors))
      for (const i in state.attributes) {
        const index = newData.errors.indexOf(`a${Number(i) + 1}`)
        0 <= index && newData.errors.splice(index, 1)
      }
      newData.attributes = JSON.parse(JSON.stringify(model.attributes))
    }
    if (!isEqual(state.variables, model.variables)) {
      newData.refs = state.refs
      newData.errors ||= JSON.parse(JSON.stringify(state.errors))
      for (const vi in state.variables) {
        delete newData.refs[`v${Number(vi) + 1}`]
        const index = newData.errors.indexOf(`v${Number(vi) + 1}`)
        0 <= index && newData.errors.splice(index, 1)
      }
      newData.variables = JSON.parse(JSON.stringify(model.variables))
    }
    return newData
  }
  const checkState = () => {
    const newData = {}
    if (!isEqual(state.entities, model.entities)) newData.entities = JSON.parse(JSON.stringify(state.entities))
    if (!isEqual(state.attributes, model.attributes)) newData.attributes = JSON.parse(JSON.stringify(state.attributes))
    if (!isEqual(state.variables, model.variables)) newData.variables = JSON.parse(JSON.stringify(state.variables))
    return newData
  }

  const checkError = () => {
    if (0 < state.errors.length || ('survey' !== model.source && !state.entities.length) || !state.attributes.length) {
      return { error: true }
    }
    if (
      'survey' !== model.source &&
      state.attributes.some(attr => attr.refs.split(',').length < state.entities.length)
    ) {
      return {
        error:
          'For each attribute, please make sure there is a question number or subquestion number that corresponds to each entity.'
      }
    }
    if ('survey' === model.source && state.attributes.some(attr => attr.refs.split(',').length < 1)) {
      return {
        error: 'For each attribute, please make sure there is one question number or subquestion number.'
      }
    }
    return { error: false }
  }

  useEffect(() => {
    if (changed) return

    dispatch({
      type: 'RESET',
      data: { ...checkModel() }
    })

    setChanged(
      !isEqual(stateRef.current.entities, model.entities) ||
        !isEqual(stateRef.current.attributes, model.attributes) ||
        !isEqual(stateRef.current.variables, model.variables)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.entities, model.attributes, model.variables])

  useEffect(() => {
    setChanged(true)

    const newData = {
      refs: state.refs,
      ...checkError(),
      ...checkState()
    }

    timer.current && clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      onChange(newData)

      setChanged(false)
    }, 300)

    return () => timer.current && clearTimeout(timer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  useEffect(() => {
    if (0 < data.controls.waves.options.length && !data.controls.waves.options.some(v => v.key == waveRef.current))
      onControlsChange('wave', data.controls.waves.options[data.controls.waves.options.length - 1].key)
  }, [data.controls.waves.options, onControlsChange])

  const dataContextValue = useMemo(() => {
    return {
      id: model.id,
      mode: model.mode,
      group: model.group,
      original: model.original,
      data,
      disabled,
      attributesOptions,
      dispatch
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.id, model.mode, model.group, model.original, data, disabled, attributesOptions, dispatch])

  return (
    <ComponentContainer
      Controls={Controls}
      Footer={Footer}
      model={model}
      data={data}
      states={states}
      state={state}
      prefix="Tracker"
      command="insights.publishTrackerLab"
      changed={changed}
      disabled={disabled}
      dataContextValue={dataContextValue}
      onControlsChange={onControlsChange}
      onUpdateData={onUpdateData}
      onFetchApiKey={onFetchApiKey}
      onPublish={onPublish}
      onPublished={onPublished}
    >
      {!disabled && 0 < data.controls.waves.options.length ? (
        <div className={`${style.row} ${style.controls} p-fluid p-col-12`}>
          <Dropdown
            className="p-dropdown-rounded p-field p-right p-col-4"
            placeholder={data.controls.waves.placeholder}
            value={String(model.wave)}
            options={data.controls.waves.options}
            optionValue="key"
            optionLabel="text"
            itemTemplate={itemTemplate}
            valueTemplate={valueTemplate}
            fixedDropdownDir="bottom"
            onChange={event => onControlsChange('wave', Number(event.value))}
          />
        </div>
      ) : null}
      {'survey' === model.source ? (
        <>
          {showReset ? (
            <div className={`${style.row} ${style['row-header']} p-fluid p-col-12`}>
              <div className="p-field p-right p-col-8">
                <span className={style.toggle} onClick={onReset}>
                  reset
                </span>
              </div>
            </div>
          ) : null}
          <Attributes items={state.attributes} dimension={1} />
        </>
      ) : (
        <>
          <Sources
            source={model.source}
            data={data}
            entities={state.entities}
            disabled={disabled}
            dispatch={dispatch}
          />
          <Entities items={state.entities} imagesDisabled={true} onReset={onReset} />
          <Attributes items={state.attributes} dimension={state.entities.length} onModeChange={onModeChange} />
        </>
      )}
      <Variables items={state.variables} note="for filtering and data splits" />
    </ComponentContainer>
  )
}

Component.propTypes = {
  model: PropTypes.object,
  data: PropTypes.object,
  states: PropTypes.object,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onReset: PropTypes.func,
  onControlsChange: PropTypes.func,
  onModeChange: PropTypes.func,
  onUpdateData: PropTypes.func,
  onFetchApiKey: PropTypes.func,
  onPublish: PropTypes.func,
  onPublished: PropTypes.func
}

Component.displayName = 'Component'

export default Component
